import * as React from 'react';
import { Tab, ITabProps } from './Tab';
import { Panel, IPanelProps } from './Panel';
import { nullFn } from '../../../utils';

interface ITabsContext {
  activeTab: string;
  setActiveTab: (label: string) => void;
}

interface ITabsProps {
  initial: string;
  dest?: string;
  changeTab?: (value: string) => void;
  onTabChange?: (value: string) => void;
  children?: React.ReactNode;
}

interface ITabsComp {
  Tab: React.FC<ITabProps>;
  Panel: React.FC<IPanelProps>;
}

const TabsContext = React.createContext<ITabsContext | undefined>(undefined);
TabsContext.displayName = 'TabsContext';

const Tabs: React.FC<ITabsProps> & ITabsComp = ({
  initial,
  dest,
  onTabChange = nullFn,
  children,
}) => {
  const [activeTab, setActiveTab] = React.useState(initial);

  const memoContextValue = React.useMemo(
    () => ({
      activeTab,
      setActiveTab,
    }),
    [activeTab, setActiveTab],
  );

  React.useEffect(() => {
    onTabChange(activeTab);
  }, [activeTab, onTabChange]);

  React.useEffect(() => {
    if (dest) {
      setActiveTab(dest);
    }
  }, [dest]);

  return (
    <TabsContext.Provider value={memoContextValue}>
      <>{children}</>
    </TabsContext.Provider>
  );
};

export const useTabs = (): ITabsContext => {
  const context = React.useContext(TabsContext);
  if (!context) {
    throw new Error('This component must be used within a <Tabs> component');
  }
  return context;
};

Tabs.Tab = Tab;
Tabs.Panel = Panel;

export { Tabs };
